import React, { useState, useEffect } from 'react';
import pluralize from 'pluralize';

import Glyph from 'components/Glyph';
import Heading from 'components/Heading';
import LinkBase from 'components/LinkBase';
import MediaObject from 'components/MediaObject';
import Text from 'components/Text';
import Divider from 'components/Divider';
import Row from 'components/Row';
import TextWithSubtext from 'components/TextWithSubtext';

import {
  MessageTranslationSummary_api_messageTranslationSummary_summary as TranslationSummary,
  MessageTranslationSummary_api_messageTranslationSummary_summary_preferredLanguagesDelivered as LanguageDelivered,
  MessageTranslationSummary_api_messageTranslationSummary_summary_preferredLanguagesNotDelivered as LanguageNotDelivered,
} from 'src/chat/components/DeliveryModal/__generated__/MessageTranslationSummary';

import './TranslationDeliveryModalSection.scss';

type Props = { summary: TranslationSummary };

function LanguageList({
  languages,
  originalLanguageCode,
  title,
  testId,
}: {
  languages: LanguageDelivered[] | LanguageNotDelivered[];
  originalLanguageCode: string;
  title: string;
  testId: string;
}) {
  const languageNames = new Intl.DisplayNames(['en'], {
    type: 'language',
  });

  return (
    <div className="TranslationDeliveryModalSection-list" data-testid={testId}>
      <Heading level="4">
        <Text>{title}</Text>
      </Heading>
      {languages.map((language) => {
        return (
          <div key={language.languageCode} data-testid={language.languageCode}>
            <Divider size="xxsmall" />
            <div className="TranslationDeliveryModalSection-item">
              {language.translationIntent === 'off' ? (
                <TextWithSubtext
                  subtle
                  subtext="Translation turned off by sender"
                >{`${languageNames.of(language.languageCode)} (Off)`}</TextWithSubtext>
              ) : (
                <Text>{`${languageNames.of(language.languageCode)} ${
                  language.languageCode === originalLanguageCode
                    ? '(Original)'
                    : language.translationIntent === 'edited'
                    ? '(Edited)'
                    : ''
                }`}</Text>
              )}
              <Text>{`${language.recipientsCount} ${pluralize(
                'recipients',
                language.recipientsCount
              )}`}</Text>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TranslationDeliveryModalSection(props: Props) {
  const { summary } = props;

  const [isFolded, setIsFolded] = useState(true);

  const topRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isFolded) {
      topRef?.current?.scrollIntoView();
    }
  }, [isFolded]);

  const {
    noPreferredLanguageRecipientsCount,
    preferredLanguagesDeliveredCount,
    preferredLanguageRecipientsCount,
    originalLanguageCode,
    preferredLanguagesDelivered,
    preferredLanguagesNotDelivered,
  } = summary;

  return (
    <div className="TranslationDeliveryModalSection" data-testid="TranslationDeliveryModalSection">
      <div ref={topRef} />
      <LinkBase
        className="TranslationDeliveryModalSection-header"
        onClick={() => setIsFolded(!isFolded)}
      >
        <div>
          <div>
            <Heading level="4">
              <MediaObject gutter="xsmall" center>
                <Text>Languages delivered</Text>
                {(preferredLanguagesDelivered || preferredLanguagesNotDelivered) && (
                  <Glyph
                    data-testid="fold-icon"
                    solo
                    size="small"
                    color="mascot"
                    name={isFolded ? 'chevron-down' : 'chevron-up'}
                  />
                )}
              </MediaObject>
            </Heading>
          </div>
          <div>
            <Text color="graphite" size="xxsmall">
              {`${preferredLanguagesDeliveredCount} ${pluralize(
                'languages',
                preferredLanguagesDeliveredCount
              )} ${pluralize(
                'were',
                preferredLanguagesDeliveredCount
              )} used to translate this message to ${
                preferredLanguageRecipientsCount !== null ? preferredLanguageRecipientsCount : ''
              } ${pluralize(
                'people',
                preferredLanguageRecipientsCount || 0
              )} in their preferred language.`}
              {preferredLanguageRecipientsCount && !isFolded
                ? " These numbers reflect the recipients' current preferred languages."
                : ''}
            </Text>
          </div>
        </div>
        <div className="TranslationDeliveryModalSection-header-count">
          <Text size="xlarge">{preferredLanguagesDeliveredCount}</Text>
        </div>
      </LinkBase>
      <div className="TranslationDeliveryModalSection-fold">
        {!isFolded && preferredLanguagesDelivered && (
          <LanguageList
            testId="delivered-list"
            title="Preferred language received"
            languages={preferredLanguagesDelivered}
            originalLanguageCode={originalLanguageCode}
          />
        )}
        {!isFolded && preferredLanguagesNotDelivered && (
          <LanguageList
            testId="not-delivered-list"
            title="Preferred language not received"
            languages={preferredLanguagesNotDelivered}
            originalLanguageCode={originalLanguageCode}
          />
        )}
        {!isFolded && noPreferredLanguageRecipientsCount && (
          <Row>
            <Divider size="xxsmall" />
            <div className="TranslationDeliveryModalSection-item" data-testid="no-preferred">
              <Text>No preferred language</Text>
              <Text>{`${noPreferredLanguageRecipientsCount} ${pluralize(
                'recipients',
                noPreferredLanguageRecipientsCount
              )}`}</Text>
            </div>
          </Row>
        )}
      </div>
    </div>
  );
}

export default TranslationDeliveryModalSection;
